import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IndianRupee, TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  CheckCircle, Clock, AlertTriangle, XCircle, Receipt, ArrowRight
} from 'lucide-react';
import { api } from '../lib/api';
import { formatCurrency } from '../lib/utils';

interface FeeInvoice {
  id: number;
  invoice_no: string;
  student_name: string;
  student_id: number;
  total_amount: number;
  paid_amount: number;
  due_amount: number;
  status: string;
  class_name: string;
  section_name: string;
  batch_name: string;
}

interface PaymentSummaryProps {
  classId?: string;
  onClassClick?: (className: string) => void;
  showClassBreakdown?: boolean;
}

const classColors = [
  { accent: 'from-purple-500 to-purple-600', text: 'text-purple-600 dark:text-purple-400', bar: 'bg-purple-500' },
  { accent: 'from-electric-500 to-electric-600', text: 'text-electric-600 dark:text-electric-400', bar: 'bg-electric-500' },
  { accent: 'from-cyan-500 to-cyan-600', text: 'text-cyan-600 dark:text-cyan-400', bar: 'bg-cyan-500' },
  { accent: 'from-gold-500 to-gold-600', text: 'text-gold-600 dark:text-gold-400', bar: 'bg-gold-500' },
  { accent: 'from-tangerine-500 to-tangerine-600', text: 'text-tangerine-600 dark:text-tangerine-400', bar: 'bg-tangerine-500' },
  { accent: 'from-lime-500 to-lime-600', text: 'text-lime-600 dark:text-lime-400', bar: 'bg-lime-500' },
  { accent: 'from-rose-500 to-rose-600', text: 'text-rose-600 dark:text-rose-400', bar: 'bg-rose-500' },
];

export default function PaymentSummary({ classId, onClassClick, showClassBreakdown = true }: PaymentSummaryProps) {
  const [expandedClass, setExpandedClass] = useState<string | null>(null);

  const { data: invoicesData, isLoading } = useQuery({
    queryKey: ['payment-invoices', classId],
    queryFn: async () => {
      let url = '/api/fees/invoices?pageSize=500';
      if (classId) {
        // We'll filter client-side since the API doesn't support classId filter directly
      }
      const res = await api.get<FeeInvoice[]>(url);
      return res.data || [];
    },
  });

  const invoices = useMemo(() => {
    if (!invoicesData) return [];
    if (!classId) return invoicesData;
    // Filter by classId using the CLASS_NAMES mapping
    const classMap: Record<string, string> = {
      '1': 'Class 6', '2': 'Class 7', '3': 'Class 8',
      '4': 'Class 9', '5': 'Class 10', '6': 'Class 11', '7': 'Class 12',
    };
    const targetName = classMap[classId];
    return invoicesData.filter((inv) => inv.class_name === targetName);
  }, [invoicesData, classId]);

  const overall = useMemo(() => {
    const total = invoices.reduce((s, i) => s + i.total_amount, 0);
    const collected = invoices.reduce((s, i) => s + i.paid_amount, 0);
    const due = invoices.reduce((s, i) => s + i.due_amount, 0);
    return { total, collected, due, count: invoices.length };
  }, [invoices]);

  const statusCounts = useMemo(() => ({
    paid: invoices.filter((i) => i.status === 'paid').length,
    partial: invoices.filter((i) => i.status === 'partial').length,
    due: invoices.filter((i) => i.status === 'due').length,
    overdue: invoices.filter((i) => i.status === 'overdue').length,
  }), [invoices]);

  const classSummaries = useMemo(() => {
    if (classId) return [];
    const classMap = new Map<string, FeeInvoice[]>();
    invoices.forEach((inv) => {
      const key = inv.class_name || 'Unknown';
      if (!classMap.has(key)) classMap.set(key, []);
      classMap.get(key)!.push(inv);
    });
    return Array.from(classMap.entries())
      .map(([className, invs]) => {
        const total = invs.reduce((s, i) => s + i.total_amount, 0);
        const collected = invs.reduce((s, i) => s + i.paid_amount, 0);
        const due = invs.reduce((s, i) => s + i.due_amount, 0);
        return {
          className,
          total,
          collected,
          due,
          count: invs.length,
          percentage: total > 0 ? Math.round((collected / total) * 100) : 0,
        };
      })
      .sort((a, b) => a.className.localeCompare(b.className));
  }, [invoices, classId]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => <div key={i} className="glass-card p-4 rounded-xl animate-pulse h-20" />)}
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-8 glass-card rounded-xl">
        <Receipt className="w-10 h-10 text-light-text-muted mx-auto mb-2" />
        <p className="text-sm text-light-text-muted">No invoices found</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Overall Summary Cards */}
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        <div className="glass-card p-2.5 sm:p-4 rounded-xl min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <IndianRupee className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs text-light-text-muted truncate">Total</p>
          </div>
          <p className="text-[11px] sm:text-xl font-bold text-light-text dark:text-dark-text whitespace-nowrap overflow-hidden text-ellipsis leading-tight">{formatCurrency(overall.total)}</p>
          <p className="text-[8px] sm:text-[10px] text-light-text-muted mt-0.5">{overall.count} inv</p>
        </div>
        <div className="glass-card p-2.5 sm:p-4 rounded-xl min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-lime-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs text-light-text-muted truncate">Collected</p>
          </div>
          <p className="text-[11px] sm:text-xl font-bold text-lime-600 dark:text-lime-400 whitespace-nowrap overflow-hidden text-ellipsis leading-tight">{formatCurrency(overall.collected)}</p>
          <p className="text-[8px] sm:text-[10px] text-lime-600 dark:text-lime-400 mt-0.5">
            {overall.total > 0 ? Math.round((overall.collected / overall.total) * 100) : 0}%
          </p>
        </div>
        <div className="glass-card p-2.5 sm:p-4 rounded-xl min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <TrendingDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-rose-500 flex-shrink-0" />
            <p className="text-[9px] sm:text-xs text-light-text-muted truncate">Due</p>
          </div>
          <p className="text-[11px] sm:text-xl font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap overflow-hidden text-ellipsis leading-tight">{formatCurrency(overall.due)}</p>
          <p className="text-[8px] sm:text-[10px] text-rose-600 dark:text-rose-400 mt-0.5">
            {overall.total > 0 ? Math.round((overall.due / overall.total) * 100) : 0}%
          </p>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="glass-card p-3 sm:p-4 rounded-xl">
        <p className="text-xs font-semibold text-light-text dark:text-dark-text mb-2.5">Status Breakdown</p>
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Paid', count: statusCounts.paid, color: 'bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-300', icon: CheckCircle },
            { label: 'Partial', count: statusCounts.partial, color: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300', icon: Clock },
            { label: 'Due', count: statusCounts.due, color: 'bg-tangerine-100 dark:bg-tangerine-500/20 text-tangerine-700 dark:text-tangerine-300', icon: AlertTriangle },
            { label: 'Overdue', count: statusCounts.overdue, color: 'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300', icon: XCircle },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className={`rounded-lg p-2.5 text-center ${s.color}`}>
                <Icon className="w-3 h-3 mx-auto mb-0.5 opacity-60" />
                <p className="text-lg sm:text-xl font-bold">{s.count}</p>
                <p className="text-[9px] sm:text-[10px] font-medium opacity-80">{s.label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Class-wise Breakdown - only when no classId filter and showClassBreakdown */}
      {showClassBreakdown && classSummaries.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-light-text dark:text-dark-text mb-2">Class-wise Breakdown</p>
          <div className="space-y-2">
            {classSummaries.map((cls, idx) => {
              const colors = classColors[idx % classColors.length];
              const isExpanded = expandedClass === cls.className;
              return (
                <motion.div
                  key={cls.className}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass-card rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => {
                      setExpandedClass(isExpanded ? null : cls.className);
                      onClassClick?.(cls.className);
                    }}
                    className="w-full p-3 sm:p-4 text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.accent} flex items-center justify-center`}>
                          <span className="text-white text-xs font-bold">{cls.className.replace('Class ', '')}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-light-text dark:text-dark-text">{cls.className}</p>
                          <p className="text-[9px] text-light-text-muted">{cls.count} invoices</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${colors.text}`}>{cls.percentage}%</span>
                        {onClassClick && <ArrowRight className="w-3.5 h-3.5 text-light-text-muted" />}
                        {!onClassClick && (isExpanded ? <ChevronUp className="w-4 h-4 text-light-text-muted" /> : <ChevronDown className="w-4 h-4 text-light-text-muted" />)}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-1.5 mb-2.5">
                      <div className="min-w-0">
                        <p className="text-[9px] text-light-text-muted">Total</p>
                        <p className="text-[11px] sm:text-xs font-bold text-light-text dark:text-dark-text whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(cls.total)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[9px] text-lime-600 dark:text-lime-400">Collected</p>
                        <p className="text-[11px] sm:text-xs font-bold text-lime-600 dark:text-lime-400 whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(cls.collected)}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[9px] text-rose-600 dark:text-rose-400">Due</p>
                        <p className="text-[11px] sm:text-xs font-bold text-rose-600 dark:text-rose-400 whitespace-nowrap overflow-hidden text-ellipsis">{formatCurrency(cls.due)}</p>
                      </div>
                    </div>

                    <div className="w-full bg-light-elevated dark:bg-dark-elevated rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${colors.bar}`}
                        style={{ width: `${cls.percentage}%` }}
                      />
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
